/**
 * 74. Projects — list/detail; budgets; tasks; milestones; team;
 * profitability.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate, formatMoney } from "@/lib/money";

interface ProjectRow {
  id: string;
  code: string;
  name: string;
  status: "Active" | "Draft" | "OnHold" | "Completed" | "Cancelled";
  startDate: string | null;
  endDate: string | null;
  budget: string;
  actualCost: string;
  currency: string;
  profitability: string;
  manager: string;
}

export function ProjectsPage() {
  const { data, isLoading } = useApiList<ProjectRow>(["projects"], "/projects");

  return (
    <div>
      <PageHeader
        title="Projects"
        subtitle="Budgets, tasks, milestones, team, and profitability."
        actions={<Button variant="primary">New project</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Projects (${data.total})`}>
          <DataTable<ProjectRow>
            rows={data.items}
            columns={[
              {
                key: "code",
                label: "Code",
                render: (r) => <span className="atlas-mono">{r.code}</span>,
              },
              { key: "name", label: "Name" },
              { key: "manager", label: "Manager" },
              {
                key: "startDate",
                label: "Start",
                render: (r) => (r.startDate ? formatDate(r.startDate) : "—"),
              },
              {
                key: "endDate",
                label: "End",
                render: (r) => (r.endDate ? formatDate(r.endDate) : "—"),
              },
              {
                key: "budget",
                label: "Budget",
                numeric: true,
                render: (r) => formatMoney(r.budget, r.currency),
              },
              {
                key: "actualCost",
                label: "Actual",
                numeric: true,
                render: (r) => formatMoney(r.actualCost, r.currency),
              },
              {
                key: "profitability",
                label: "Profitability",
                numeric: true,
                render: (r) => {
                  const p = Number(r.profitability);
                  return (
                    <span
                      style={{
                        color:
                          p >= 0
                            ? "var(--atlas-success)"
                            : "var(--atlas-danger)",
                      }}
                    >
                      {formatMoney(r.profitability, r.currency)}
                    </span>
                  );
                },
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Active" || r.status === "Completed" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Cancelled" ? (
                    <Badge tone="danger">{r.status}</Badge>
                  ) : (
                    <Badge tone={r.status === "OnHold" ? "warning" : "default"}>
                      {r.status}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
